<template>
  <div class="export-records-container">
    <h2 class="page-title">导出记录</h2>
    <el-card shadow="hover" class="content-card">
      <el-alert
        title="导出文件保存说明"
        type="info"
        show-icon
        :closable="false"
        style="margin-bottom: 20px"
      >
        导出文件将在生成后保留3个月，请及时下载保存。超过3个月的文件将被系统自动清理，但导出记录会永久保留。
      </el-alert>
      
      <!-- 使用Tab组件 -->
      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <!-- 预约单导出记录 -->
        <el-tab-pane label="预约单" name="appointment_records">
          <div class="card-content">
            <el-table :data="exportRecords" border stripe style="width: 100%" v-loading="loading">
              <el-table-column prop="filename" label="文件名" />
              <el-table-column prop="status" label="状态" width="120">
                <template #default="scope">
                  <el-tag
                    :type="
                      scope.row.status === 'completed'
                        ? 'success'
                        : scope.row.status === 'processing'
                        ? 'warning'
                        : scope.row.status === 'failed'
                        ? 'danger'
                        : 'info'
                    "
                  >
                    {{ getStatusLabel(scope.row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="total_records" label="记录数" width="100" />
              <el-table-column prop="admin.name" label="操作人" width="120" />
              <el-table-column prop="created_at" label="创建时间" width="200">
                <template #default="scope">
                  {{ formatDateTime(scope.row.created_at) }}
                </template>
              </el-table-column>
              <el-table-column prop="updated_at" label="更新时间" width="200">
                <template #default="scope">
                  {{ formatDateTime(scope.row.updated_at) }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="200" fixed="right">
                <template #default="scope">
                  <el-button
                    v-if="scope.row.status === 'completed'"
                    type="primary"
                    size="small"
                    @click="downloadFile(scope.row.id)"
                  >
                    下载
                  </el-button>
                  <el-button
                    type="danger"
                    size="small"
                    @click="deleteRecord(scope.row.id)"
                  >
                    删除
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="pagination-container">
              <el-pagination
                :current-page="pagination.currentPage"
                :page-size="pagination.pageSize"
                :page-sizes="[10, 20, 50, 100]"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
              />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../api/axios';

// 当前激活的tab
const activeTab = ref('appointment_records');

// 导出记录列表
const exportRecords = ref([]);

// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
});

// loading状态
const loading = ref(false);

// 搜索表单（已移除筛选功能）
const searchForm = reactive({});

// 获取导出记录列表
const fetchExportRecords = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: '/manage/exports',
      method: 'get',
      params: {
        page: pagination.currentPage,
        per_page: pagination.pageSize,
        type: activeTab.value,
      },
    });

    if (response && response.data) {
      if (response.data.data && Array.isArray(response.data.data)) {
        exportRecords.value = response.data.data;
        pagination.total = response.data.total || 0;
      } else {
        exportRecords.value = Array.isArray(response.data) ? response.data : [response.data];
        pagination.total = exportRecords.value.length;
      }
    } else {
      exportRecords.value = [];
      pagination.total = 0;
    }
  } catch (error) {
    ElMessage.error('获取导出记录列表失败');
    console.error('获取导出记录列表错误:', error);
  } finally {
    loading.value = false;
  }
};

// 处理tab切换
// eslint-disable-next-line no-unused-vars
const handleTabChange = (tabName) => {
  activeTab.value = tabName;
  pagination.currentPage = 1;
  fetchExportRecords();
};

// 分页处理
// eslint-disable-next-line no-unused-vars
const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchExportRecords();
};

// eslint-disable-next-line no-unused-vars
const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchExportRecords();
};

// 获取导出类型标签
// eslint-disable-next-line no-unused-vars
const getExportTypeLabel = (type) => {
  const typeMap = {
    appointment_records: '预约单'
  };
  return typeMap[type] || type;
};

// 获取状态标签
// eslint-disable-next-line no-unused-vars
const getStatusLabel = (status) => {
  const statusMap = {
    pending: '待处理',
    processing: '处理中',
    completed: '已完成',
    failed: '失败',
  };
  return statusMap[status] || status;
};

// 格式化日期时间为YYYY-mm-dd HH:ii:ss格式
// eslint-disable-next-line no-unused-vars
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 下载文件
// eslint-disable-next-line no-unused-vars
const downloadFile = async (id) => {
  try {
    const response = await request({
      url: `/manage/exports/${id}/download`,
      method: 'get',
      responseType: 'blob', // 重要：设置响应类型为blob
    });

    // 创建下载链接
    const blob = new Blob([response.data], {
      type: 'text/csv',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // 从响应头中获取文件名，如果没有则使用默认名称
    const contentDisposition = response.headers['content-disposition'];
    let filename = `export-${id}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // 清理
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    ElMessage.success('文件下载成功');
  } catch (error) {
    ElMessage.error('文件下载失败');
    console.error('文件下载错误:', error);
  }
};

// 删除记录
// eslint-disable-next-line no-unused-vars
const deleteRecord = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除这条导出记录吗？删除后将无法恢复。', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    });

    await request({
      url: `/manage/exports/${id}`,
      method: 'delete',
    });

    ElMessage.success('删除成功');
    fetchExportRecords();
  } catch (error) {
    if (error.name !== 'ElMessageBoxCanceledError') {
      ElMessage.error('删除失败');
      console.error('删除错误:', error);
    }
  }
};

// 组件挂载时获取数据
onMounted(() => {
  fetchExportRecords();
});
</script>

<style scoped>
.export-records-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-area {
  display: flex;
  gap: 10px;
}

.card-content {
  padding: 20px 0;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
